<script setup>
import { computed, ref, watch } from 'vue';
import Card from '@pfsc/shared/components/card.vue';
import SkeletonLoader from '@pfsc/shared/components/skeleton-loader.vue';
import {
  analyticsService, ANALYTICS_PRESET, COMPARE_TO, SERIES_GROUP, SERIES_METRIC,
} from '@pfsc/core/services/analytics.service';
import PeriodBar from './components/period-bar.vue';
import RefreshStatus from './components/refresh-status.vue';
import StatTile from './components/stat-tile.vue';
import TrendChart from './components/trend-chart.vue';
import BreakdownPanel from './components/breakdown-panel.vue';
import BasketsPanel from './components/baskets-panel.vue';
import ComparePresets from './components/compare-presets.vue';
import { defaultComparePeriod, isCompareWindow } from './components/compare-windows';
import PackagePicker from './components/package-picker.vue';
import MoversPanel from './components/movers-panel.vue';
import SeriesChart from './components/series-chart.vue';
import DailyLedger from './components/daily-ledger.vue';
import PaymentMethodsPanel from './components/payment-methods-panel.vue';
import SummaryDifferences from './components/summary-differences.vue';
import BulkBuyersPanel from './components/bulk-buyers-panel.vue';

/**
 * PFS admin analytics.
 *
 * Mounted as <pfs-analytics /> by web/admin/analytics.php, inside the legacy admin chrome and
 * beside the two reports it replaces in the Administrator menu. It deliberately does NOT live in
 * the V2 portal: a PFS admin only reaches that scoped into a single group-admin account, and
 * these figures are company-wide, so showing them there would read as that one account's.
 *
 * Replaces both legacy reports. `businessSummary.php` is arithmetically broken - "FH" is a
 * residual computed as total minus two other buckets, so anything it fails to classify lands
 * there silently - and `revenueReport.php` can only show one dimension per run. Everything here
 * comes from set-based queries that classify each package explicitly, and one request returns
 * every dimension for the window.
 *
 * No number on this screen is computed in the browser. Shares, changes and totals are all the
 * server's, so this view and the native app cannot drift apart.
 */

const VIEW = {
  OVERVIEW: 'overview', DAILY: 'daily', TOGETHER: 'together', COMPARE: 'compare', BULK: 'bulk',
};

const view = ref(VIEW.OVERVIEW);
const period = ref({
  preset: ANALYTICS_PRESET.LAST_30_DAYS,
  startDate: null,
  endDate: null,
  compareTo: COMPARE_TO.PREVIOUS_PERIOD,
});

const metric = ref(SERIES_METRIC.REVENUE);
const seriesGroupBy = ref(SERIES_GROUP.TOTAL);
const CHART = { LINE: 'line', BARS: 'bars', SHARE: 'share' };
const chartType = ref(CHART.LINE);

// A split changes what the reader is asking. Over time answers "when"; bars rank; the ring shows
// the mix. Packages are compared, not trended, so they default to bars - but the choice stays
// theirs afterwards.
watch(seriesGroupBy, next => {
  if (next === SERIES_GROUP.TOTAL) {
    chartType.value = CHART.LINE;
  } else if (next === SERIES_GROUP.PACKAGE) {
    chartType.value = CHART.BARS;
  } else {
    chartType.value = CHART.SHARE;
  }
});

// One day is a single point on a line chart, so a one-day window opens on the category mix
// instead. Once the reader picks a split themselves, it is left alone.
const splitChosen = ref(false);

/**
 * Whether the window covers a single day.
 * @param {object} window Period.
 * @returns {boolean}
 */
function isSingleDay(window) {
  if (window.preset === ANALYTICS_PRESET.TODAY || window.preset === ANALYTICS_PRESET.YESTERDAY) {
    return true;
  }
  return window.preset === ANALYTICS_PRESET.CUSTOM && Boolean(window.startDate) && window.startDate === window.endDate;
}

watch(period, next => {
  if (!splitChosen.value) {
    seriesGroupBy.value = isSingleDay(next) ? SERIES_GROUP.PRODUCT_LINE : SERIES_GROUP.TOTAL;
  }
}, { deep: true, immediate: true });
const comparePackageIds = ref([]);

const daily = ref(null);

/**
 * The month the Daily tab is showing, as [year, monthIndex].
 *
 * The daily table is read a month at a time - that is what the legacy Financial Breakdown is -
 * so it carries its own month stepper rather than borrowing the period chips, which can select
 * windows a day-by-day table makes no sense for.
 */
const dailyMonth = ref([new Date().getFullYear(), new Date().getMonth()]);

const dailyPeriod = computed(() => {
  const [year, month] = dailyMonth.value;
  const pad = value => `${value}`.padStart(2, '0');
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    preset: ANALYTICS_PRESET.CUSTOM,
    startDate: `${year}-${pad(month + 1)}-01`,
    endDate: `${year}-${pad(month + 1)}-${pad(lastDay)}`,
  };
});

const dailyMonthLabel = computed(() => new Date(dailyMonth.value[0], dailyMonth.value[1], 1)
  .toLocaleDateString(undefined, { month: 'long', year: 'numeric' }));

/**
 * Step the daily month.
 * @param {number} delta Months to move.
 */
function stepMonth(delta) {
  const [year, month] = dailyMonth.value;
  const moved = new Date(year, month + delta, 1);
  dailyMonth.value = [moved.getFullYear(), moved.getMonth()];
}

const isDailyLoading = ref(false);
const dailyError = ref(null);
const dailyDenied = ref(false);
let dailyToken = 0;

/**
 * Load the day-by-day ledger for the selected month.
 * @param {boolean} refresh Rebuild the figures instead of reading the server's cache.
 */
async function loadDaily(refresh = false) {
  const token = ++dailyToken;
  isDailyLoading.value = true;
  dailyError.value = null;
  dailyDenied.value = false;
  try {
    const next = await analyticsService.getDaily({ ...dailyPeriod.value, refresh });
    if (token === dailyToken) {
      daily.value = next;
    }
  } catch (error) {
    if (token === dailyToken) {
      daily.value = null;
      dailyDenied.value = Boolean(error?.isDenied);
      dailyError.value = error?.message ?? 'We could not load the daily breakdown.';
    }
  } finally {
    if (token === dailyToken) {
      isDailyLoading.value = false;
    }
  }
}

/**
 * One request and its state. Each run is stamped, so a slow response for an older window can
 * never overwrite the figures of a newer one.
 * @param {Function} fetch Request to make.
 * @returns {object} The data, its loading and error state, and how to run or clear it.
 */
function createLoader(fetch) {
  const data = ref(null);
  const isRunning = ref(false);
  const error = ref(null);
  const denied = ref(false);
  let token = 0;

  /**
   * Run the request, ignoring the result if another run started meanwhile.
   * @param {...any} args Arguments for the request.
   */
  async function run(...args) {
    const mine = ++token;
    isRunning.value = true;
    error.value = null;
    denied.value = false;
    try {
      const next = await fetch(...args);
      if (mine === token) {
        data.value = next;
      }
    } catch (failure) {
      if (mine === token) {
        denied.value = Boolean(failure?.isDenied);
        error.value = failure?.message ?? 'We could not load analytics. Please try again.';
      }
    } finally {
      if (mine === token) {
        isRunning.value = false;
      }
    }
  }

  /** Drop the data and discard any request still in flight. */
  function clear() {
    token += 1;
    data.value = null;
    isRunning.value = false;
    error.value = null;
    denied.value = false;
  }

  return {
    data, isRunning, error, denied, run, clear,
  };
}

// The headline and the breakdowns are separate requests: the headline answers quickly and the
// page is usable while the slower breakdowns are still being built.
const summaryLoader = createLoader(analyticsService.getOverviewSummary);
const detailsLoader = createLoader(analyticsService.getOverviewDetails);
const basketsLoader = createLoader(analyticsService.getBaskets);
/** Package-filtered figures for the Compare view. Unused when comparing the whole business. */
const filteredLoader = createLoader(analyticsService.getOverview);

const {
  data: summary, isRunning: isLoading, error: loadError, denied: isDenied,
} = summaryLoader;
const { data: details, isRunning: isDetailsLoading, error: detailsError } = detailsLoader;
const { data: baskets, isRunning: isBasketsLoading, error: basketsError } = basketsLoader;
const { data: filtered, isRunning: isFilteredLoading, error: filteredError } = filteredLoader;

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
});
const number = new Intl.NumberFormat('en-US');

/**
 * Render a window as a human date range.
 * @param {object} range Period.
 * @returns {string}
 */
function formatRange(range) {
  if (!range) {
    return '';
  }
  const format = value => new Date(`${value}T00:00:00`)
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return range.startDate === range.endDate
    ? format(range.startDate)
    : `${format(range.startDate)} – ${format(range.endDate)}`;
}

/**
 * Render one date.
 * @param {string} value Y-m-d.
 * @returns {string}
 */
function formatDate(value) {
  return new Date(`${value}T00:00:00`)
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const periodLabel = computed(() => formatRange(summary.value?.period));

const bookings = computed(() => summary.value?.bookings ?? null);
const change = computed(() => summary.value?.comparison?.change ?? null);

/**
 * Percent change for one metric, or null when there is nothing to compare against.
 * @param {string} key Metric key.
 * @returns {number|null}
 */
function changeFor(key) {
  return change.value?.[key]?.percent ?? null;
}

const nonOrder = computed(() => details.value?.nonOrderCollections ?? null);

/**
 * Set when the preceding window reaches back before orders were recorded, so its bookings are
 * missing rather than low and no percentage change can be shown against it.
 */
const comparisonGap = computed(() => summary.value?.comparison?.coverageGap ?? null);

const hasSales = computed(() => Boolean(bookings.value) && bookings.value.orders > 0);

/**
 * The window every request is built on. The unfiltered window is always fetched: Overview and
 * Combos are whole-business views, and the package picker's options come from it, so
 * they stay stable as the selection changes rather than collapsing to whatever is selected.
 * @param {boolean} refresh Rebuild the figures instead of reading the server's cache.
 * @returns {object}
 */
function windowRequest(refresh = false) {
  return { ...period.value, compare: true, refresh };
}

/**
 * Fetch the breakdowns, which take longer than the headline.
 * @param {boolean} refresh Rebuild in place: the figures on screen stay until the new ones land.
 */
function loadDetails(refresh = false) {
  if (!refresh) {
    detailsLoader.clear();
  }
  detailsLoader.run({ ...windowRequest(refresh), limit: 100, seriesGroupBy: seriesGroupBy.value });
}

/** The request the Compare view's filtered figures on screen were fetched with. */
let filteredRequestKey = null;

/**
 * Fetch the Compare view's package-filtered figures, or drop them once nothing is picked.
 *
 * Only the Compare view asks for them. The filtered request is the heaviest the dashboard makes,
 * and the period it would run for on any other tab is one Compare never chose.
 * @param {boolean} refresh Rebuild in place: the figures on screen stay until the new ones land.
 */
function loadFiltered(refresh = false) {
  if (comparePackageIds.value.length === 0) {
    filteredRequestKey = null;
    filteredLoader.clear();
    return;
  }
  if (view.value !== VIEW.COMPARE) {
    return;
  }

  const request = { ...windowRequest(), limit: 100, packageIds: comparePackageIds.value };
  const key = JSON.stringify(request);

  if (!refresh && key === filteredRequestKey && (filtered.value || isFilteredLoading.value)) {
    return;
  }
  if (!refresh) {
    filteredLoader.clear();
  }
  filteredRequestKey = key;
  filteredLoader.run({ ...request, refresh });
}

/**
 * Load everything the current window needs, the headline and the breakdowns in parallel.
 * @param {boolean} refresh Rebuild the figures instead of reading the server's cache.
 */
function load(refresh = false) {
  summaryLoader.run(windowRequest(refresh));
  if (!refresh) {
    basketsLoader.clear();
  }
  basketsLoader.run({
    ...period.value, compare: true, limit: 100, refresh,
  });
  loadDetails(refresh);
  loadFiltered(refresh);
}

watch(period, () => load(), { deep: true, immediate: true });
// A new split only changes the breakdowns, so the headline stays put.
watch(seriesGroupBy, () => loadDetails());
watch(comparePackageIds, () => loadFiltered(), { deep: true });

// Compare reads the same period as Overview, which can be a year long. Opening it from a window
// that is not one of its own presets lands on this month, so a preset is always selected and the
// package-filtered request runs once, for that window.
watch(view, next => {
  if (next !== VIEW.COMPARE) {
    return;
  }
  if (isCompareWindow(period.value)) {
    loadFiltered();
  } else {
    period.value = defaultComparePeriod();
  }
});

// Fetched only when the tab is actually looked at, and again whenever the month moves.
watch([view, dailyMonth], () => {
  if (view.value === VIEW.DAILY) {
    loadDaily();
  }
}, { deep: true, immediate: true });

/** Re-run the current view's requests, asking the server to rebuild instead of answering from its cache. */
function refreshView() {
  if (view.value === VIEW.DAILY) {
    loadDaily(true);
  } else {
    load(true);
  }
}

const isRefreshing = computed(() => (view.value === VIEW.DAILY
  ? isDailyLoading.value
  : isLoading.value || isDetailsLoading.value || isBasketsLoading.value || isFilteredLoading.value));

/** Refreshing is for people who can see the figures; a denied view has nothing to refresh. */
const canRefresh = computed(() => {
  if (view.value === VIEW.BULK) {
    return false;
  }
  return view.value === VIEW.DAILY ? !dailyDenied.value : !isDenied.value;
});

/** Views that fetch their own data, so the headline summary's loading and error states do not apply. */
const hasOwnRequests = computed(() => view.value === VIEW.DAILY || view.value === VIEW.BULK);

/**
 * When the figures on the current view were built. A view drawn from several requests reports
 * the oldest of them, since that is how stale the page can be.
 */
const refreshedAt = computed(() => {
  const sources = {
    [VIEW.OVERVIEW]: [summary.value, details.value],
    [VIEW.DAILY]: [daily.value],
    [VIEW.BULK]: [],
    [VIEW.TOGETHER]: [baskets.value],
    [VIEW.COMPARE]: [summary.value, details.value, comparePackageIds.value.length > 0 ? filtered.value : null],
  }[view.value];
  const times = sources
    .map(source => source?.meta?.generatedAt)
    .filter(Boolean)
    .map(value => new Date(value).getTime())
    .filter(value => !Number.isNaN(value));

  return times.length > 0 ? new Date(Math.min(...times)) : null;
});

/**
 * Headline and breakdowns together, for the views that need both. Null until the two halves
 * describe the same window.
 */
const overview = computed(() => {
  if (!summary.value || !details.value || isLoading.value) {
    return null;
  }
  return {
    ...summary.value,
    ...details.value,
    series: { ...summary.value.series, ...details.value.series },
    comparison: summary.value.comparison
      ? { ...details.value.comparison, ...summary.value.comparison }
      : null,
  };
});

/** The window the Compare view reports on - filtered when packages are picked. */
const compareSource = computed(() => (comparePackageIds.value.length > 0 ? filtered.value : overview.value));

/** Every day in the window, so split series share one axis. */
const chartDays = computed(() => (summary.value?.series.bookings ?? []).map(row => row.day));

/** A split chart needs the breakdowns; the single total line is drawn from the headline alone. */
const chartNeedsDetails = computed(() => seriesGroupBy.value !== SERIES_GROUP.TOTAL
  && !(details.value && details.value.series.grouped.length === 0));

/** One line, or several, depending on the breakdown. */
const chartSeries = computed(() => {
  if (!summary.value) {
    return [];
  }
  if (!details.value || seriesGroupBy.value === SERIES_GROUP.TOTAL || details.value.series.grouped.length === 0) {
    return [{ key: 'total', label: metric.value === SERIES_METRIC.REVENUE ? 'Revenue' : 'Registrations', points: summary.value.series.bookings }];
  }
  // Ten overlapping lines are unreadable, so the line chart is capped. Bars and the ring stay
  // legible however many rows there are, and truncating them would misstate the shares.
  return chartType.value === CHART.LINE
    ? details.value.series.grouped.slice(0, 8)
    : details.value.series.grouped;
});

/**
 * Percent change on the Compare view, which may be reading a package-filtered window.
 * @param {string} key Metric key as the server names it.
 * @returns {number|null}
 */
function compareChange(key) {
  return compareSource.value?.comparison?.change?.[key]?.percent ?? null;
}

const hiddenSeriesCount = computed(() => (chartType.value === CHART.LINE
  ? Math.max(0, (details.value?.series.grouped.length ?? 0) - chartSeries.value.length)
  : 0));
</script>

<template>
  <div :class="$style.wrapper">
    <nav :class="$style.views">
      <button
        v-for="option in [
          { value: VIEW.OVERVIEW, label: 'Overview' },
          { value: VIEW.DAILY, label: 'Daily' },
          { value: VIEW.TOGETHER, label: 'Combos' },
          { value: VIEW.COMPARE, label: 'Compare periods' },
          { value: VIEW.BULK, label: 'Bulk buyers' },
        ]"
        :key="option.value"
        type="button"
        :class="[$style.viewTab, view === option.value ? $style.viewTabActive : '']"
        @click="view = option.value"
      >
        {{ option.label }}
      </button>
    </nav>

    <refresh-status
      v-if="canRefresh"
      :generated-at="refreshedAt"
      :is-refreshing="isRefreshing"
      @refresh="refreshView"
    />

    <div v-if="view === VIEW.DAILY" :class="$style.monthBar">
      <button type="button" :class="$style.monthStep" aria-label="Previous month" @click="stepMonth(-1)">
        ‹
      </button>
      <span :class="$style.monthLabel">{{ dailyMonthLabel }}</span>
      <button type="button" :class="$style.monthStep" aria-label="Next month" @click="stepMonth(1)">
        ›
      </button>
      <span v-if="isDailyLoading" :class="$style.monthLoading">Loading…</span>
    </div>
    <compare-presets v-else-if="view === VIEW.COMPARE" v-model="period" />
    <period-bar v-else v-model="period" :period-label="periodLabel" />

    <template v-if="view === VIEW.DAILY">
      <card v-if="dailyError" :class="$style.messageCard">
        <p :class="$style.errorTitle">
          {{ dailyDenied ? 'You do not have access to analytics' : "We couldn't load the daily breakdown" }}
        </p>
        <p :class="$style.messageBody">
          {{ dailyDenied ? 'These figures are limited to PFS administrators.' : dailyError }}
        </p>
        <button
          v-if="!dailyDenied"
          type="button"
          class="primary"
          :class="$style.retry"
          @click="loadDaily()"
        >
          Try again
        </button>
      </card>
      <card v-else-if="isDailyLoading && !daily">
        <skeleton-loader />
      </card>
      <daily-ledger
        v-else
        :ledger="daily"
        :is-loading="isDailyLoading"
      />
    </template>

    <bulk-buyers-panel v-if="view === VIEW.BULK" :period="period" />

    <div v-if="isLoading && !hasOwnRequests" :class="$style.progress" role="status" aria-live="polite">
      <span :class="$style.progressBar" />
      <span :class="$style.progressText">Loading {{ periodLabel || 'analytics' }}…</span>
    </div>

    <card v-if="!hasOwnRequests && isLoading && !summary">
      <skeleton-loader />
    </card>

    <card v-else-if="loadError && !hasOwnRequests" :class="$style.messageCard">
      <p :class="$style.errorTitle">
        {{ isDenied ? 'You do not have access to analytics' : "We couldn't load analytics" }}
      </p>
      <p :class="$style.messageBody">
        {{ isDenied ? 'These figures are limited to PFS administrators.' : loadError }}
      </p>
      <button
        v-if="!isDenied"
        type="button"
        class="primary"
        :class="$style.retry"
        @click="load()"
      >
        Try again
      </button>
    </card>

    <div v-else-if="summary && !hasOwnRequests" :class="[$style.results, isLoading ? $style.stale : '']">
      <p v-if="summary.coverageGap" :class="$style.coverage">
        {{ summary.coverageGap.message }}
      </p>

      <template v-if="view === VIEW.OVERVIEW">
        <card v-if="!hasSales" :class="$style.messageCard">
          <p :class="$style.messageTitle">
            No paid orders in this range
          </p>
          <p :class="$style.messageBody">
            Money collected in the same window was {{ money.format(summary.collections.collected) }}.
          </p>
        </card>

        <template v-else>
        <div :class="$style.tiles">
          <stat-tile
            label="Revenue booked"
            hint="Orders placed in this period"
            :value="moneyCompact.format(bookings.revenue)"
            :change-percent="changeFor('revenue')"
            tone="teal"
          />
          <stat-tile
            v-if="summary.collections"
            label="Collected"
            hint="Money received in this period, as on the old Summary page"
            :value="moneyCompact.format(summary.collections.collected)"
            :change-percent="changeFor('collected')"
            tone="teal"
          />
          <stat-tile
            label="Orders"
            :value="number.format(bookings.orders)"
            :change-percent="changeFor('orders')"
            tone="teal"
          />
          <stat-tile
            label="Registrations"
            :value="number.format(bookings.units)"
            :change-percent="changeFor('units')"
            tone="orange"
          />
          <stat-tile
            label="Avg order"
            :value="money.format(bookings.averageOrderValue)"
            :change-percent="changeFor('average_order_value')"
            tone="red"
          />
        </div>

        <p v-if="comparisonGap" :class="$style.coverage">
          No order history for the comparison period (before
          {{ formatDate(comparisonGap.complete_from) }}), so changes in revenue, orders and registrations are not shown.
        </p>

        <div :class="$style.card">
          <div :class="$style.chartHead">
            <h3 :class="$style.cardTitle">
              {{ metric === SERIES_METRIC.REVENUE ? 'Revenue' : 'Registrations' }} over time
            </h3>

            <div :class="$style.controls">
              <label :class="$style.control">
                <span :class="$style.controlLabel">Measure</span>
                <select v-model="metric" :class="$style.select">
                  <option :value="SERIES_METRIC.REVENUE">Revenue</option>
                  <option :value="SERIES_METRIC.UNITS">Registrations</option>
                </select>
              </label>

              <label :class="$style.control">
                <span :class="$style.controlLabel">Split by</span>
                <select
                  v-model="seriesGroupBy"
                  :class="$style.select"
                  @change="splitChosen = true"
                >
                  <option :value="SERIES_GROUP.TOTAL">Everything</option>
                  <option :value="SERIES_GROUP.PRODUCT_LINE">Category</option>
                  <option :value="SERIES_GROUP.LINE_DELIVERY">Category — in person / online</option>
                  <option :value="SERIES_GROUP.DELIVERY">In person / online</option>
                  <option :value="SERIES_GROUP.PACKAGE">Package</option>
                </select>
              </label>

              <label v-if="seriesGroupBy !== SERIES_GROUP.TOTAL" :class="$style.control">
                <span :class="$style.controlLabel">Chart</span>
                <select v-model="chartType" :class="$style.select">
                  <option :value="CHART.LINE">Over time</option>
                  <option :value="CHART.BARS">Bars</option>
                  <option :value="CHART.SHARE">Share</option>
                </select>
              </label>
            </div>
          </div>

          <div v-if="chartNeedsDetails && !details" :class="$style.chartWait">
            <template v-if="detailsError">
              <p :class="$style.messageBody">
                {{ detailsError }}
              </p>
              <button
                type="button"
                class="primary"
                :class="$style.retry"
                @click="loadDetails()"
              >
                Try again
              </button>
            </template>
            <skeleton-loader v-else :number-of-skeletons="1" />
          </div>
          <trend-chart
            v-else-if="chartType === CHART.LINE"
            :days="chartDays"
            :series="chartSeries"
            :metric="metric"
            :collected="summary.series.collections"
          />
          <series-chart
            v-else
            :series="chartSeries"
            :mode="chartType"
            :metric="metric"
          />

          <p v-if="hiddenSeriesCount" :class="$style.moneyNote">
            Showing the {{ chartSeries.length }} largest. {{ hiddenSeriesCount }} smaller
            {{ hiddenSeriesCount === 1 ? 'series is' : 'series are' }} not drawn.
          </p>
        </div>

        </template>

        <div v-if="summary.collections" :class="$style.card">
          <h3 :class="$style.cardTitle">
            Money
          </h3>
          <dl :class="$style.moneyList">
            <div :class="$style.moneyRow">
              <dt>Product sales</dt>
              <dd>{{ money.format(summary.bookings.gross) }}</dd>
            </div>
            <div v-if="summary.bookings.orderDiscount" :class="$style.moneyRow">
              <dt>Order discounts</dt>
              <dd>-{{ money.format(summary.bookings.orderDiscount) }}</dd>
            </div>
            <div :class="[$style.moneyRow, $style.moneyRowStrong]">
              <dt>Revenue booked</dt>
              <dd>{{ money.format(summary.bookings.revenue) }}</dd>
            </div>
            <div :class="[$style.moneyRow, $style.moneyRowDivided]">
              <dt>Collected</dt>
              <dd>{{ money.format(summary.collections.collected) }}</dd>
            </div>
            <div :class="$style.moneyRow">
              <dt>
                Refunded
                <span v-if="summary.collections.refundCount" :class="$style.subtle">
                  ({{ number.format(summary.collections.refundCount) }})
                </span>
              </dt>
              <dd :class="summary.collections.refunded ? $style.negative : ''">
                {{ summary.collections.refunded ? '-' : '' }}{{ money.format(summary.collections.refunded) }}
              </dd>
            </div>
            <div :class="[$style.moneyRow, $style.moneyRowStrong]">
              <dt>Net cash</dt>
              <dd>{{ money.format(summary.collections.net) }}</dd>
            </div>
          </dl>
          <p v-if="isDetailsLoading" :class="$style.moneyNote">
            Checking for collections with no order behind them…
          </p>
          <div v-else-if="nonOrder && nonOrder.total.payments > 0" :class="$style.noOrder">
            <p :class="$style.noOrderLine">
              Collected with no order behind it:
              <strong>{{ money.format(nonOrder.total.amount) }}</strong>
              (Apex site {{ money.format(nonOrder.apexSite.amount) }}, other {{ money.format(nonOrder.other.amount) }})
            </p>
            <details v-if="nonOrder.other.amount > 0" :class="$style.noOrderDetails">
              <summary>Other, by category</summary>
              <ul :class="$style.noOrderList">
                <li v-for="row in nonOrder.other.byCategory" :key="row.categoryId ?? 'none'">
                  <span>{{ row.label }}</span>
                  <span>
                    {{ number.format(row.payments) }} {{ row.payments === 1 ? 'payment' : 'payments' }}
                    · {{ money.format(row.amount) }}
                  </span>
                </li>
              </ul>
            </details>
          </div>

          <payment-methods-panel
            v-if="summary.paymentsByMethod.length"
            :methods="summary.paymentsByMethod"
            :wallets="summary.paymentWallets"
          />

          <p :class="$style.moneyNote">
            Booked counts what sold on the order date. Collected counts money that arrived on the
            payment date, so the two rarely match inside one window.
          </p>
        </div>

        <template v-if="details">
          <summary-differences />

          <breakdown-panel
            v-if="hasSales"
            :product-lines="details.productLines"
            :markets="details.markets"
            :packages="details.packages"
            :package-count="details.packageCount"
          />
        </template>

        <card v-else-if="detailsError" :class="$style.messageCard">
          <p :class="$style.errorTitle">
            We couldn't load the breakdowns
          </p>
          <p :class="$style.messageBody">
            {{ detailsError }}
          </p>
          <button
            type="button"
            class="primary"
            :class="$style.retry"
            @click="loadDetails()"
          >
            Try again
          </button>
        </card>

        <template v-else>
          <card v-for="n in (hasSales ? 2 : 1)" :key="n">
            <skeleton-loader :number-of-skeletons="1" />
          </card>
        </template>
      </template>

      <card v-else-if="!hasSales" :class="$style.messageCard">
        <p :class="$style.messageTitle">
          No paid orders in this range
        </p>
        <p :class="$style.messageBody">
          Money collected in the same window was {{ money.format(summary.collections.collected) }}.
        </p>
      </card>

      <template v-else-if="view === VIEW.TOGETHER">
        <card v-if="basketsError" :class="$style.messageCard">
          <p :class="$style.errorTitle">
            We couldn't load the combinations
          </p>
          <p :class="$style.messageBody">
            {{ basketsError }}
          </p>
          <button
            type="button"
            class="primary"
            :class="$style.retry"
            @click="load()"
          >
            Try again
          </button>
        </card>
        <card v-else-if="isBasketsLoading && !baskets">
          <skeleton-loader />
        </card>
        <baskets-panel
          v-else-if="baskets"
          :cart="baskets.cart"
          :comparison-label="formatRange(baskets.cart.comparison?.period)"
        />
      </template>

      <template v-else>
        <card v-if="detailsError" :class="$style.messageCard">
          <p :class="$style.errorTitle">
            We couldn't load the comparison
          </p>
          <p :class="$style.messageBody">
            {{ detailsError }}
          </p>
          <button
            type="button"
            class="primary"
            :class="$style.retry"
            @click="loadDetails()"
          >
            Try again
          </button>
        </card>
        <card v-else-if="!overview">
          <skeleton-loader />
        </card>
        <template v-else>
        <div :class="$style.card">
          <h3 :class="$style.cardTitle">
            Narrow the comparison
          </h3>
          <package-picker v-model="comparePackageIds" :product-lines="overview.productLines" />
        </div>

        <card v-if="comparePackageIds.length > 0 && filteredError" :class="$style.messageCard">
          <p :class="$style.errorTitle">
            We couldn't load the filtered comparison
          </p>
          <p :class="$style.messageBody">
            {{ filteredError }}
          </p>
          <button
            type="button"
            class="primary"
            :class="$style.retry"
            @click="loadFiltered()"
          >
            Try again
          </button>
        </card>
        <card v-else-if="comparePackageIds.length > 0 && isFilteredLoading">
          <skeleton-loader />
        </card>
        <template v-else>
        <div v-if="compareSource && compareSource.comparison" :class="$style.card">
          <h3 :class="$style.cardTitle">
            {{ formatRange(compareSource.period) }}
            <span :class="$style.versus">vs</span>
            {{ formatRange(compareSource.comparison.period) }}
          </h3>
          <p :class="$style.moneyNote">
            <template v-if="compareSource.comparison.mode === COMPARE_TO.PREVIOUS_CALENDAR">
              The previous calendar period, whole — so the day counts can differ by one or two.
            </template>
            <template v-else-if="compareSource.comparison.mode?.startsWith('same_span_')">
              The same dates one {{ compareSource.comparison.mode.replace('same_span_last_', '') }} earlier,
              so both windows cover the same part of the cycle.
            </template>
            <template v-else>
              The preceding window of the same length, so the day counts match exactly.
            </template>
          </p>

          <table :class="$style.compareTable">
            <thead>
              <tr>
                <th scope="col">
                  Metric
                </th>
                <th scope="col">
                  This window
                </th>
                <th scope="col">
                  Previous
                </th>
                <th scope="col">
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in [
                  { key: 'revenue', field: 'revenue', label: 'Revenue', format: money.format },
                  { key: 'units', field: 'units', label: 'Registrations', format: number.format },
                  { key: 'orders', field: 'orders', label: 'Orders', format: number.format },
                  { key: 'buyers', field: 'buyers', label: 'Buyers', format: number.format },
                  { key: 'average_order_value', field: 'averageOrderValue', label: 'Avg order', format: money.format },
                ]"
                :key="row.key"
              >
                <th scope="row">
                  {{ row.label }}
                </th>
                <td>{{ row.format(compareSource.bookings[row.field]) }}</td>
                <td>{{ row.format(compareSource.comparison.bookings[row.field]) }}</td>
                <td v-if="compareChange(row.key) === null" :class="$style.zero">
                  —
                </td>
                <td v-else :class="compareChange(row.key) >= 0 ? $style.up : $style.down">
                  {{ compareChange(row.key) > 0 ? '+' : '' }}{{ compareChange(row.key).toFixed(1) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="compareSource && compareSource.comparison && compareSource.comparison.coverageGap" :class="$style.coverage">
          No order history for the comparison period (before
          {{ formatDate(compareSource.comparison.coverageGap.complete_from) }}), so percentage changes and the
          risers and fallers are not shown.
        </p>
        <movers-panel
          v-else-if="compareSource && compareSource.comparison"
          :movers="compareSource.comparison.movers"
          :product-lines="overview.productLines"
        />
        </template>
        </template>
      </template>
    </div>
  </div>
</template>

<style module>
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 18px;
  flex: 1;
}
.views {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
  contain: inline-size;
}
.viewTab {
  flex: none;
  white-space: nowrap;
  border: none;
  background: transparent;
  padding: 0 0 9px;
  margin-bottom: -1px;
  border-bottom: 2px solid transparent;
  font-size: var(--font-size-xl);
  font-weight: 500;
  color: var(--primary-font-color);
  cursor: pointer;
}
.viewTab:hover {
  color: var(--primary-font-dark-color);
}
.viewTabActive {
  color: var(--pfs-red);
  border-bottom-color: var(--pfs-red);
  font-weight: 600;
}
.subtle {
  color: var(--faint-color);
}
.monthBar {
  display: flex;
  align-items: center;
  gap: 10px;
}
.monthStep {
  border: 1px solid var(--border-color);
  background: white;
  border-radius: var(--border-radius);
  width: 32px;
  height: 32px;
  font-size: 18px;
  line-height: 1;
  color: var(--primary-font-dark-color);
  cursor: pointer;
}
.monthStep:hover {
  border-color: var(--pfs-red);
  color: var(--pfs-red);
}
.monthLabel {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
  min-width: 12ch;
  text-align: center;
}
.monthLoading {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.progress {
  display: flex;
  align-items: center;
  gap: 10px;
}
.progressBar {
  position: relative;
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: var(--border-color-light);
  overflow: hidden;
}
.progressBar::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 35%;
  border-radius: 2px;
  background: var(--pfs-red);
  animation: analytics-progress 1.1s ease-in-out infinite;
}
@keyframes analytics-progress {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(320%); }
}
@media (prefers-reduced-motion: reduce) {
  .progressBar::after { animation: none; width: 100%; opacity: 0.4; }
}
.progressText {
  font-size: var(--font-size-md);
  color: var(--faint-color);
  white-space: nowrap;
}
.results {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
@media (max-width: 640px) {
  .views {
    gap: 18px;
  }
  .card {
    padding: 14px;
  }
  .compareTable th,
  .compareTable td {
    padding: 8px 5px;
  }
}
/* The figures on screen belong to the previous window until the new ones land. */
.stale {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.chartHead {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  align-items: flex-end;
  justify-content: space-between;
}
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.control {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.controlLabel {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.select {
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 6px 9px;
  font: inherit;
  font-size: var(--font-size-lg);
  background: white;
}
.negative {
  color: var(--button-red);
}
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
}
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cardTitle {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.versus {
  color: var(--faint-color);
  font-weight: 400;
  padding: 0 4px;
}
.moneyList {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.moneyRow {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--primary-font-color);
}
.moneyRow dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.moneyRowStrong {
  font-weight: 700;
  color: var(--primary-font-dark-color);
}
.moneyRowDivided {
  padding-top: 10px;
  border-top: 1px solid var(--border-color-light);
}
.moneyNote {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.compareTable {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-lg);
}
.compareTable th,
.compareTable td {
  text-align: right;
  padding: 9px 8px;
  border-bottom: 1px solid var(--border-color-light);
  font-variant-numeric: tabular-nums;
}
.compareTable thead th {
  color: var(--faint-color);
  font-weight: 500;
  font-size: var(--font-size-md);
}
.compareTable th[scope="row"],
.compareTable thead th:first-child {
  text-align: left;
  font-weight: 500;
  color: var(--primary-font-dark-color);
}
.zero { color: var(--faint-color); }
.up { color: var(--green-light); }
.down { color: var(--button-red); }

.messageCard {
  text-align: center;
  padding: 28px 20px;
}
.messageTitle, .errorTitle {
  margin: 0 0 6px;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.errorTitle {
  color: var(--button-red);
}
.messageBody {
  margin: 0;
  color: var(--faint-color);
  font-size: var(--font-size-lg);
}
.retry {
  margin-top: 14px;
  padding: 9px 22px;
}
.noOrder {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color-light);
}
.noOrderLine {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--primary-font-color);
}
.noOrderDetails {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.noOrderDetails summary {
  cursor: pointer;
}
.noOrderList {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.noOrderList li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-variant-numeric: tabular-nums;
}
.chartWait {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  min-height: 120px;
  text-align: center;
}
.coverage {
  margin: 0;
  padding: 11px 14px;
  border-radius: var(--border-radius-md);
  background: var(--scorm-progress-bg);
  border: 1px solid var(--scorm-progress-border);
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
}
</style>
